import { useState } from 'react';
import { Check, Lock, UserMinus, UserPlus } from 'lucide-react';
import {
  useNutzerVerwaltung,
  useNutzerAendern,
  useNutzerAktivSetzen
} from '@/hooks/useNutzerVerwaltung';
import type { NutzerVerwaltung, NutzerPools } from '@/hooks/useNutzerVerwaltung';
import { NutzerChip } from '@/components/ui/NutzerChip';
import { NUTZER_SYMBOLE, nutzerSymbol } from '@/lib/nutzerSymbol';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { FELD_KLASSE } from '@/lib/feldKlassen';
import { useToast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

/**
 * Mitarbeiter pflegen — der erste Abschnitt der Einstellungen (A36).
 *
 * DAS MUSTER, DEM DIE NÄCHSTEN LISTEN FOLGEN
 *   Ein Abschnitt ist: eine Überschrift, ein Satz, der sagt woher die
 *   Einträge kommen, und darunter je Eintrag eine Zeile, die sich zum
 *   Formular aufklappt. Gespeichert wird je Eintrag, nicht für die ganze
 *   Seite — sonst weiß nach dem Speichern niemand, was alles mitging.
 *
 * HIER WIRD NICHTS ANGELEGT UND NICHTS GELÖSCHT
 *   Mitarbeiter entstehen beim ersten Anmelden über Nextcloud. Ein Knopf
 *   „Mitarbeiter anlegen" erzeugte einen Nutzer, den niemand benutzen
 *   kann. Und wer geht, hat Bestellungen erfasst — sein Name steht an
 *   Vorgängen und im Protokoll.
 *
 * WARUM DIE ZEILE SICH AUFKLAPPT UND NICHT ALLES OFFEN STEHT
 *   Bei acht Mitarbeitern stünden sonst 32 Felder untereinander. Die
 *   Frage, die man hier hat, ist fast immer eine einzelne („welche Farbe
 *   hat Bernd?"), und die beantwortet die zugeklappte Zeile schon.
 */
export function MitarbeiterAbschnitt() {
  const { data, isLoading, isError } = useNutzerVerwaltung();

  return (
    <section aria-labelledby="einst-mitarbeiter" className="space-y-3">
      <div>
        <h2 id="einst-mitarbeiter" className="font-serif text-lg text-ink">
          Mitarbeiter
        </h2>
        <p className="mt-1 text-sm text-ink-soft">
          Wer sich einmal über Nextcloud angemeldet hat, steht hier. Angelegt und
          gelöscht wird niemand in dieser Ansicht — Name, Kürzel, Farbe und Symbol
          lassen sich ändern. Wer geht, wird stillgelegt: Er verschwindet aus den
          Auswahllisten, bleibt aber an seinen Bestellungen und im Protokoll.
        </p>
      </div>

      {isLoading && (
        <p className="flex items-center gap-2 text-sm text-ink-soft">
          <Spinner /> Mitarbeiter werden geladen …
        </p>
      )}
      {isError && (
        <p className="text-sm text-coral">Die Mitarbeiterliste ließ sich nicht laden.</p>
      )}

      {/* `?? []` und nicht `data.nutzer.map`: Antwortet der Server ohne
          das Feld — ältere Fassung im Umlauf, ein Fehlerpfad, ein
          Prüfstand mit anderer Attrappe —, würde die ganze Seite
          mitgerissen. Ein Abschnitt, der leer bleibt, ist ein Ärgernis;
          eine Einstellungsseite, die deshalb weiß bleibt, ist ein
          Ausfall (die Lehre aus A31). */}
      {data !== undefined && (
        <ul className="space-y-2">
          {(data.nutzer ?? []).map((n) => (
            <li key={n.id}>
              <MitarbeiterZeile
                nutzer={n}
                alle={data.nutzer ?? []}
                pools={data.pools ?? { farben: [], symbole: [] }}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function MitarbeiterZeile({
  nutzer,
  alle,
  pools
}: {
  nutzer: NutzerVerwaltung;
  alle: NutzerVerwaltung[];
  pools: NutzerPools;
}) {
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState(nutzer.display_name ?? '');
  const [kuerzel, setKuerzel] = useState(nutzer.kuerzel ?? '');
  const [farbe, setFarbe] = useState(nutzer.farbe);
  const [symbol, setSymbol] = useState(nutzer.symbol);
  const aendern = useNutzerAendern();
  const aktivSetzen = useNutzerAktivSetzen();
  const toast = useToast();
  const auth = useAuth();
  const binIchSelbst = auth.data?.user?.id === nutzer.id;

  /**
   * Farben, die schon jemand anderes trägt.
   *
   * Stillgelegte zählen NICHT mit (A42) — ihre Farbe ist wieder frei,
   * und der Server sieht es genauso. Zählte die Oberfläche sie weiter,
   * zeigte sie eine Farbe als belegt an, die sich sehr wohl setzen
   * lässt: zwei Meinungen über dieselbe Sache.
   */
  const belegt = new Set(
    alle
      .filter((a) => a.id !== nutzer.id && a.aktiv && a.farbe !== null)
      .map((a) => a.farbe as string)
  );
  /**
   * Kürzel, die jemand anderes trägt — gerechnet oder gewählt.
   *
   * Nur ein Hinweis, keine Sperre: Im Bestand können heute schon zwei
   * Leute dieselben Initialen haben, und eine Oberfläche, die das
   * verbietet, ließe sich dann nicht mehr benutzen. Wer es sieht, kann
   * es ändern — darum geht es hier.
   */
  const kuerzelDoppelt = alle.some(
    (a) => a.id !== nutzer.id && a.kuerzel_effektiv === (kuerzel.trim().toUpperCase() || nutzer.kuerzel_effektiv)
  );

  function speichern(): void {
    aendern.mutate(
      {
        id: nutzer.id,
        display_name: name.trim(),
        kuerzel: kuerzel.trim(),
        ...(farbe === null ? {} : { farbe }),
        symbol: symbol ?? ''
      },
      {
        onSuccess: (d) => {
          // Sichtbar, DASS gespeichert wurde — wie überall sonst über
          // den Melder (so macht es die Bestellseite auch). Keine
          // Rückfrage davor: Die Angaben sind jederzeit wieder änderbar.
          toast(
            'success',
            d.geaendert.length === 0
              ? 'Nichts zu ändern.'
              : `Gespeichert: ${d.nutzer.display_name ?? d.nutzer.nc_username}.`
          );
          setOffen(false);
        },
        onError: (e) => {
          const code = e instanceof ApiError ? e.code : '';
          toast(
            'error',
            code === 'farbe_belegt'
              ? 'Diese Farbe trägt schon jemand anderes.'
              : code === 'farbe_unbekannt'
                ? 'Diese Farbe steht nicht zur Wahl.'
                : code === 'ungueltig'
                  ? 'Bitte die Eingaben prüfen.'
                  : 'Speichern fehlgeschlagen.'
          );
        }
      }
    );
  }

  return (
    <div className="rounded-md border border-line bg-cream">
      {/* Die zugeklappte Zeile beantwortet die häufigste Frage schon:
          wer ist das, welche Farbe, welches Zeichen. */}
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="flex min-h-[44px] w-full items-center gap-3 px-3 py-2 text-left"
      >
        <NutzerChip
          farbe={nutzer.farbe}
          symbol={nutzer.symbol}
          kuerzel={nutzer.kuerzel_effektiv}
          name={nutzer.display_name ?? nutzer.nc_username}
        />
        <span className="min-w-0 flex-1">
          {/* DAS ABZEICHEN STEHT NEBEN DEM TEXT, NICHT DARIN (A43, #120).
              Bis hierher saß es INNERHALB des abschneidenden Spans. Bei
              390 Pixeln und einem langen Namen lagen von seinen 70,8
              Pixeln nur 18,7 innerhalb der Kante — und auch die zeigten
              nichts, weil dort das Auslassungszeichen steht. Gemessen:
              der Treffertest auf seinen eigenen Mittelpunkt fand es
              nicht mehr.

              Ausgerechnet die Angabe, wegen der man hinsieht, fehlte
              also. Jetzt schrumpft der NAME (`min-w-0 truncate`) und
              das Abzeichen bleibt (`shrink-0`) — abgeschnitten wird,
              was man auch aufgeklappt noch lesen kann. */}
          <span className="flex items-center gap-2">
            <span className="min-w-0 truncate text-sm text-ink">
              {nutzer.display_name ?? nutzer.nc_username}
            </span>
            {/* Das WORT sagt es, nicht bloß eine blasse Schrift: Eine
                Unterscheidung, die nur an der Farbe hängt, sagt in der
                Sonne und für manche Augen gar nichts (A24). */}
            {!nutzer.aktiv && (
              <span className="shrink-0 rounded border border-line px-1.5 py-0.5 text-xs text-ink-soft">
                stillgelegt
              </span>
            )}
          </span>
          <span className="block truncate text-xs text-ink-soft">
            {nutzer.kuerzel_effektiv}
            {nutzer.kuerzel === null && ' (aus dem Namen)'} · {nutzer.nc_username}
          </span>
        </span>
        <span className="shrink-0 text-xs text-akzent">{offen ? 'Schließen' : 'Ändern'}</span>
      </button>

      {offen && (
        <div className="space-y-3 border-t border-line px-3 py-3">
          <label className="block text-xs text-ink-soft">
            Anzeigename
            <input
              type="text"
              value={name}
              maxLength={255}
              onChange={(e) => setName(e.target.value)}
              className={`mt-1 ${FELD_KLASSE}`}
            />
          </label>

          <label className="block text-xs text-ink-soft">
            Kürzel <span className="text-ink-faint">(leer lassen für {kuerzelVorgabe(nutzer)})</span>
            <input
              type="text"
              value={kuerzel}
              maxLength={4}
              onChange={(e) => setKuerzel(e.target.value.toUpperCase())}
              placeholder={kuerzelVorgabe(nutzer)}
              className={`mt-1 ${FELD_KLASSE} tabular-nums`}
            />
          </label>
          {kuerzelDoppelt && (
            <p className="text-xs text-coral">
              Dieses Kürzel trägt schon jemand anderes. Eine @-Erwähnung erreicht dann
              beide.
            </p>
          )}

          <fieldset>
            <legend className="text-xs text-ink-soft">Farbe</legend>
            <div className="mt-1 flex flex-wrap gap-1">
              {/* Der Bestand kennt Farben, die es im Pool nicht (mehr)
                  gibt — in der Sandbox tragen zwei von drei Nutzern
                  Teal und Gold aus den Anfangsdaten. Ohne diese Kachel
                  stünde die eigene Farbe nirgends, und es sähe aus, als
                  hätte niemand eine. Sie ist nicht wählbar: Wer sie
                  verlässt, soll nicht versehentlich zurückkönnen. */}
              {farbe !== null && !pools.farben.includes(farbe) && (
                <span
                  className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-gold"
                  title="Aktuelle Farbe — steht nicht mehr zur Wahl"
                >
                  <span
                    className="h-7 w-7 rounded-full"
                    style={{ backgroundColor: farbe }}
                    aria-label={`${farbe} — aktuelle Farbe, nicht mehr im Pool`}
                    role="img"
                  />
                </span>
              )}
              {pools.farben.map((f) => (
                <FarbKnopf
                  key={f}
                  farbe={f}
                  gewaehlt={farbe === f}
                  belegt={belegt.has(f)}
                  onWahl={() => setFarbe(f)}
                />
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-xs text-ink-soft">Symbol</legend>
            <div className="mt-1 flex flex-wrap gap-1">
              {pools.symbole.map((code) => (
                <SymbolKnopf
                  key={code}
                  code={code}
                  gewaehlt={symbol === code}
                  onWahl={() => setSymbol(symbol === code ? null : code)}
                />
              ))}
            </div>
          </fieldset>

          {/* Der Anmeldename steht da, aber gesperrt. */}
          <label className="block text-xs text-ink-soft">
            Anmeldename in Nextcloud
            <span className="mt-1 flex items-center gap-2 rounded-md border border-line bg-cream-warm px-3 py-2 text-sm text-ink-soft">
              <Lock aria-hidden className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{nutzer.nc_username}</span>
            </span>
            <span className="mt-1 block text-ink-faint">
              Lässt sich nicht ändern: Daran hängt die Identität aus Nextcloud und mit
              ihr jede Bestellung, die dieser Mensch erfasst hat.
            </span>
          </label>

          {/* Stilllegen (A42) — abgesetzt von den Angaben darüber, weil
              es keine Angabe ist, sondern eine Aussage darüber, ob
              dieser Mensch noch hier arbeitet. Es speichert SOFORT und
              hängt nicht am „Speichern" der Felder: Sonst legte ein
              Klick auf Speichern nebenbei jemanden still. */}
          <div className="space-y-2 rounded-md border border-line bg-cream-warm px-3 py-2">
            <p className="text-xs text-ink-soft">
              {nutzer.aktiv
                ? 'Arbeitet hier. Steht in der Erfasser-Auswahl, in den @-Vorschlägen und hält seine Farbe.'
                : 'Stillgelegt. Steht in keiner Auswahlliste mehr; seine Bestellungen, das Protokoll und geschriebene Erwähnungen bleiben unverändert.'}
            </p>
            <Button
              variant="ghost"
              disabled={aktivSetzen.isPending || (nutzer.aktiv && binIchSelbst)}
              onClick={() =>
                aktivSetzen.mutate(
                  { id: nutzer.id, aktiv: !nutzer.aktiv },
                  {
                    onSuccess: (d) =>
                      toast(
                        'success',
                        d.nutzer.aktiv
                          ? `${d.nutzer.display_name ?? d.nutzer.nc_username} arbeitet wieder mit.`
                          : `${d.nutzer.display_name ?? d.nutzer.nc_username} ist stillgelegt.`
                      ),
                    onError: (e) =>
                      toast(
                        'error',
                        e instanceof ApiError && e.code === 'selbst_stilllegen'
                          ? 'Du kannst Dich nicht selbst stilllegen.'
                          : 'Ließ sich nicht ändern.'
                      )
                  }
                )
              }
            >
              {aktivSetzen.isPending ? <Spinner /> : nutzer.aktiv ? (
                <UserMinus aria-hidden className="mr-1.5 h-4 w-4" />
              ) : (
                <UserPlus aria-hidden className="mr-1.5 h-4 w-4" />
              )}
              {nutzer.aktiv ? 'Stilllegen' : 'Wieder aktivieren'}
            </Button>
            {nutzer.aktiv && binIchSelbst && (
              /* Der Grund steht DA, statt dass der Knopf nur grau ist:
                 Ein gesperrter Knopf ohne Erklärung liest sich wie ein
                 Fehler. */
              <p className="text-xs text-ink-faint">
                Dich selbst kannst Du nicht stilllegen — sonst käme niemand mehr an diese
                Einstellung heran.
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={speichern} disabled={aendern.isPending}>
              {aendern.isPending ? <Spinner /> : null}
              Speichern
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setName(nutzer.display_name ?? '');
                setKuerzel(nutzer.kuerzel ?? '');
                setFarbe(nutzer.farbe);
                setSymbol(nutzer.symbol);
                setOffen(false);
              }}
            >
              Abbrechen
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Was ohne eigenes Kürzel gilt — die Initialen aus dem Namen. */
function kuerzelVorgabe(n: NutzerVerwaltung): string {
  return n.kuerzel === null ? n.kuerzel_effektiv : '…';
}

/**
 * Eine Farbe zur Wahl.
 *
 * Die Farbe steht im `style`, nicht in einer Klasse: Sie kommt aus der
 * Datenbank und kann gar keine Klasse sein. (Wer eine Zusicherung
 * darüber schreibt, vergleicht deshalb den Wert, nicht das
 * class-Attribut — Befunde aus A27, A31 und A33.)
 *
 * Belegte Farben bleiben SICHTBAR und werden nur abgewiesen, wenn man
 * sie antippt. Sie ganz zu verstecken hieße: Wer wissen will, warum
 * seine Wunschfarbe fehlt, findet keine Antwort.
 */
function FarbKnopf({
  farbe,
  gewaehlt,
  belegt,
  onWahl
}: {
  farbe: string;
  gewaehlt: boolean;
  belegt: boolean;
  onWahl: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onWahl}
      disabled={belegt && !gewaehlt}
      aria-pressed={gewaehlt}
      aria-label={belegt && !gewaehlt ? `${farbe} — schon vergeben` : farbe}
      title={belegt && !gewaehlt ? 'Schon vergeben' : undefined}
      className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-line disabled:opacity-40"
    >
      <span
        className="flex h-7 w-7 items-center justify-center rounded-full"
        style={{ backgroundColor: farbe }}
      >
        {gewaehlt && <Check aria-hidden className="h-4 w-4 text-cream" />}
      </span>
    </button>
  );
}

function SymbolKnopf({
  code,
  gewaehlt,
  onWahl
}: {
  code: string;
  gewaehlt: boolean;
  onWahl: () => void;
}) {
  const s = nutzerSymbol(code) ?? NUTZER_SYMBOLE[0];
  const Icon = s.Icon;
  return (
    <button
      type="button"
      onClick={onWahl}
      aria-pressed={gewaehlt}
      aria-label={s.name}
      title={s.name}
      className={`inline-flex h-11 w-11 items-center justify-center rounded-md border ${
        gewaehlt ? 'border-akzent bg-akzent-sanft text-akzent-stark' : 'border-line text-ink-soft'
      }`}
    >
      <Icon aria-hidden className="h-5 w-5" />
    </button>
  );
}
